/* eslint-disable @typescript-eslint/camelcase */
import {
  UseMutationOptions,
  UseMutationResult,
  useMutation,
  UseQueryOptions,
  UseQueryResult,
  useQuery,
}                           from 'react-query';
import { AxiosError }       from 'axios';
import {
  callApi,
  requestAttachments,
}                           from '@utils/apiCaller';
import { Notification }     from '@components/Notification/Notification';
import { showErrorMessage } from '@utils/commonFunctions';
import {
  INewCommentPayload,
  IComment,
  IDocument,
  IFetchCommentsPayload,
}                           from '@typings/comments';
import { store }            from '@store/index';
import { getToken }         from '@modules/auth/authReducer';

// #region SAVE COMMENT
const saveComment = async ({ type, id, comment }: INewCommentPayload): Promise<IComment> => {
  try {
    const { documentFile, message } = comment;

    const formData = new FormData();

    formData.append('message', message);

    if (documentFile) {
      formData.append('file', documentFile);
    }

    const { data } = await callApi<IComment>(
      `comments/${type}/${id}`,
      'post',
      formData,
      { isFormData: true },
    );

    Notification.enqueueSnackbar(
      'Comment has been saved.',
      'info',
    );

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useSaveComment = (
  config: UseMutationOptions<IComment, AxiosError, INewCommentPayload> = {},
): UseMutationResult<IComment, AxiosError, INewCommentPayload> => useMutation(
  saveComment,
  config,
);
// #endregion

// #region FETCH COMMENTS
const fetchAllComments = async ({ type, id }: IFetchCommentsPayload): Promise<IComment[]> => {
  try {
    const { data } = await callApi<IComment[]>(`comments/${type}/${id}`);

    return data;
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );

    throw e;
  }
};

export const useFetchComments = (
  payload : IFetchCommentsPayload,
  config? : UseQueryOptions<IComment[], AxiosError>,
): UseQueryResult<IComment[], AxiosError> => useQuery(
  'fetch-all-comments',
  () => fetchAllComments(payload),
  {
    retry                : false,
    refetchOnWindowFocus : false,
    ...config,
  },
);
// #endregion

// #region FETCH DOCUMENT
const fetchDocument = async ({
  id,
  fileName,
}: IDocument): Promise<void> => {
  try {
    const state = store.createdStore.getState();
    const access_token = getToken(state);

    await requestAttachments(
      `document/${id}/${fileName}`,
      'attachment',
      { access_token },
    );
  } catch (e) {
    Notification.enqueueSnackbar(
      showErrorMessage(e),
      'error',
    );
  }
};

export const useFetchDocument = (
  config: UseMutationOptions<void, AxiosError, IDocument> = {},
): UseMutationResult<void, AxiosError, IDocument> => useMutation(
  fetchDocument,
  config,
);
// #endregion

/* eslint-enable @typescript-eslint/camelcase */
